import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { Button, Input, TextArea } from '../shared';
import ModalShell from '../shared/ModalShell';
import { VENDOR_PAGE_ACCESS_GROUPS, VENDOR_PAGE_ACCESS_OPTIONS } from '../../utils/vendorPlanAccess';
import { getPlatformSettlementCurrency } from '../../services/settlementCurrencyService';

const emptyForm = (currency = 'USD') => ({
  name: '',
  description: '',
  features_list: [''],
  price_monthly: '',
  price_yearly: '',
  currency,
  max_bookings_per_month: '',
  max_services: '',
  max_portfolio_items: '',
  max_images: '',
  max_packages_per_service: '',
  max_ai_generations_per_month: '',
  feature_analytics: false,
  feature_custom_branding: false,
  feature_priority_support: false,
  feature_api_access: false,
  feature_advanced_booking: false,
  feature_multi_location: false,
  feature_ai_generator: false,
  is_active: true,
  sort_order: 0,
  stripe_product_id: '',
  stripe_price_id_monthly: '',
  stripe_price_id_yearly: '',
  vendor_page_access: VENDOR_PAGE_ACCESS_OPTIONS.map((option) => option.key),
});

const Section = ({ title, hint, children }) => (
  <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-950/40 p-4 sm:p-5 space-y-4">
    <div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50 tracking-tight">{title}</h3>
      {hint ? <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{hint}</p> : null}
    </div>
    {children}
  </section>
);

const CreateSubscriptionModal = ({
  isOpen,
  onClose,
  onSave,
  subscription = null,
  isEdit = false,
  isLoading = false,
}) => {
  const [settlementCurrency, setSettlementCurrency] = useState('USD');
  const [formData, setFormData] = useState(() => emptyForm());
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isLoading) setIsSubmitting(false);
  }, [isLoading]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    getPlatformSettlementCurrency()
      .then((data) => {
        if (cancelled || !data?.currency) return;
        setSettlementCurrency(data.currency);
        setFormData((prev) => ({ ...prev, currency: data.currency }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && subscription) {
      setFormData({
        name: subscription.name || '',
        description: subscription.description || '',
        features_list:
          subscription.features_list && subscription.features_list.length > 0
            ? subscription.features_list
            : [''],
        price_monthly:
          subscription.price_monthly != null ? subscription.price_monthly : subscription.price || '',
        price_yearly: subscription.price_yearly != null ? subscription.price_yearly : '',
        currency: settlementCurrency,
        max_bookings_per_month: subscription.max_bookings_per_month ?? '',
        max_services: subscription.max_services ?? '',
        max_portfolio_items: subscription.max_portfolio_items ?? '',
        max_images: subscription.max_images ?? '',
        max_packages_per_service: subscription.max_packages_per_service ?? '',
        max_ai_generations_per_month: subscription.max_ai_generations_per_month ?? '',
        feature_analytics: !!subscription.feature_analytics,
        feature_custom_branding: !!subscription.feature_custom_branding,
        feature_priority_support: !!subscription.feature_priority_support,
        feature_api_access: !!subscription.feature_api_access,
        feature_advanced_booking: !!subscription.feature_advanced_booking,
        feature_multi_location: !!subscription.feature_multi_location,
        feature_ai_generator: !!subscription.feature_ai_generator,
        is_active: subscription.is_active !== undefined ? subscription.is_active : true,
        sort_order: subscription.sort_order || 0,
        stripe_product_id: subscription.stripe_product_id || '',
        stripe_price_id_monthly:
          subscription.stripe_price_id_monthly || subscription.stripe_price_id || '',
        stripe_price_id_yearly: subscription.stripe_price_id_yearly || '',
        vendor_page_access: Array.isArray(subscription.vendor_page_access)
          ? subscription.vendor_page_access
          : VENDOR_PAGE_ACCESS_OPTIONS.map((option) => option.key),
      });
      setErrors({});
      return;
    }
    setFormData(emptyForm(settlementCurrency));
    setErrors({});
  }, [isOpen, isEdit, subscription, settlementCurrency]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFeatureChange = (index, value) => {
    const next = [...formData.features_list];
    next[index] = value;
    setFormData((prev) => ({ ...prev, features_list: next }));
  };

  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features_list: [...prev.features_list, ''],
    }));
  };

  const removeFeature = (index) => {
    if (formData.features_list.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      features_list: prev.features_list.filter((_, i) => i !== index),
    }));
  };

  const toggleAccess = (accessKey) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.vendor_page_access) ? prev.vendor_page_access : [];
      const next = current.includes(accessKey)
        ? current.filter((key) => key !== accessKey)
        : [...current, accessKey];
      return { ...prev, vendor_page_access: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || isLoading) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const newErrors = {};
      if (!formData.name.trim()) newErrors.name = 'Name is required';

      const pm = parseFloat(formData.price_monthly);
      const py = parseFloat(formData.price_yearly);
      if (
        formData.price_monthly === '' ||
        formData.price_monthly == null ||
        Number.isNaN(pm) ||
        pm < 0
      ) {
        newErrors.price_monthly = 'Monthly price is required (0 or greater)';
      }
      if (
        formData.price_yearly === '' ||
        formData.price_yearly == null ||
        Number.isNaN(py) ||
        py < 0
      ) {
        newErrors.price_yearly = 'Yearly price is required (0 or greater)';
      }

      const filteredFeatures = formData.features_list.filter((feature) => feature.trim() !== '');
      if (filteredFeatures.length === 0) {
        newErrors.features_list = 'At least one feature is required';
      }

      const stripePricePattern = /^price_[A-Za-z0-9]{14,}$/;
      const stripeProductPattern = /^prod_[A-Za-z0-9]{8,}$/;
      if (
        formData.stripe_price_id_monthly?.trim() &&
        !stripePricePattern.test(formData.stripe_price_id_monthly.trim())
      ) {
        newErrors.stripe_price_id_monthly =
          'Paste the full Stripe Price ID (starts with price_).';
      }
      if (
        formData.stripe_price_id_yearly?.trim() &&
        !stripePricePattern.test(formData.stripe_price_id_yearly.trim())
      ) {
        newErrors.stripe_price_id_yearly =
          'Paste the full Stripe Price ID (starts with price_).';
      }
      if (
        formData.stripe_product_id?.trim() &&
        !stripeProductPattern.test(formData.stripe_product_id.trim())
      ) {
        newErrors.stripe_product_id = 'Paste the full Stripe Product ID (starts with prod_).';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setIsSubmitting(false);
        return;
      }

      await onSave({
        ...formData,
        price_monthly: parseFloat(formData.price_monthly),
        price_yearly: parseFloat(formData.price_yearly),
        max_bookings_per_month: formData.max_bookings_per_month
          ? parseInt(formData.max_bookings_per_month, 10)
          : null,
        max_services: formData.max_services ? parseInt(formData.max_services, 10) : null,
        max_portfolio_items: formData.max_portfolio_items
          ? parseInt(formData.max_portfolio_items, 10)
          : null,
        max_images: formData.max_images ? parseInt(formData.max_images, 10) : null,
        max_packages_per_service: formData.max_packages_per_service
          ? parseInt(formData.max_packages_per_service, 10)
          : null,
        max_ai_generations_per_month:
          formData.max_ai_generations_per_month === '' ||
          formData.max_ai_generations_per_month == null
            ? null
            : parseInt(formData.max_ai_generations_per_month, 10),
        features_list: filteredFeatures,
        sort_order: parseInt(formData.sort_order, 10) || 0,
        stripe_product_id: formData.stripe_product_id?.trim() || null,
        stripe_price_id_monthly: formData.stripe_price_id_monthly?.trim() || null,
        stripe_price_id_yearly: formData.stripe_price_id_yearly?.trim() || null,
        vendor_page_access: formData.vendor_page_access,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isLoading || isSubmitting) return;
    setFormData(emptyForm(settlementCurrency));
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  const busy = isLoading || isSubmitting;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      labelledBy="subscription-modal-title"
      panelClassName="max-w-4xl"
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden flex flex-col max-h-[min(90vh,900px)]">
        <div className="shrink-0 flex items-start justify-between gap-4 px-5 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className="min-w-0 pr-2">
            <h2
              id="subscription-modal-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-50 tracking-tight"
            >
              {isEdit ? 'Edit plan' : 'New plan'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {isEdit
                ? 'Update pricing, limits, and vendor access for this SaaS plan.'
                : 'Create a vendor subscription plan with limits and Stripe mapping.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="shrink-0 p-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 !rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Section title="Basic information" hint="How this plan appears to vendors.">
                <Input
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={errors.name}
                  placeholder="e.g. Basic, Pro, Premium"
                  required
                />
                <TextArea
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Short summary of who this plan is for"
                  rows={3}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Price (monthly)"
                    name="price_monthly"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_monthly}
                    onChange={handleInputChange}
                    error={errors.price_monthly}
                    placeholder="0.00"
                    required
                  />
                  <Input
                    label="Price (yearly)"
                    name="price_yearly"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_yearly}
                    onChange={handleInputChange}
                    error={errors.price_yearly}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <Input
                    label="Currency"
                    name="currency"
                    value={formData.currency || settlementCurrency}
                    onChange={() => {}}
                    disabled
                  />
                  <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    Locked to platform settlement currency ({settlementCurrency}). Change it in Settings
                    → Settlement.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Plan is active</span>
                </label>
              </Section>

              <Section
                title="Limits"
                hint="Leave a field empty for unlimited. Sort order controls plan listing."
              >
                <Input
                  label="Max bookings / month"
                  name="max_bookings_per_month"
                  type="number"
                  min="0"
                  value={formData.max_bookings_per_month}
                  onChange={handleInputChange}
                  placeholder="Unlimited"
                />
                <Input
                  label="Max services"
                  name="max_services"
                  type="number"
                  min="0"
                  value={formData.max_services}
                  onChange={handleInputChange}
                  placeholder="Unlimited"
                />
                <Input
                  label="Max portfolio items"
                  name="max_portfolio_items"
                  type="number"
                  min="0"
                  value={formData.max_portfolio_items}
                  onChange={handleInputChange}
                  placeholder="Unlimited"
                />
                <Input
                  label="Max uploaded images"
                  name="max_images"
                  type="number"
                  min="0"
                  value={formData.max_images}
                  onChange={handleInputChange}
                  placeholder="Unlimited"
                />
                <Input
                  label="Max packages per service"
                  name="max_packages_per_service"
                  type="number"
                  min="0"
                  value={formData.max_packages_per_service}
                  onChange={handleInputChange}
                  placeholder="Unlimited"
                />
                <div>
                  <Input
                    label="AI writes per month"
                    name="max_ai_generations_per_month"
                    type="number"
                    min="0"
                    value={formData.max_ai_generations_per_month}
                    onChange={handleInputChange}
                    placeholder="Unlimited"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {formData.feature_ai_generator
                      ? 'Blank is unlimited on this plan. 0 allows no writes.'
                      : 'Applies when AI service writer is turned on.'}
                  </p>
                </div>
                <Input
                  label="Sort order"
                  name="sort_order"
                  type="number"
                  min="0"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  placeholder="0"
                />
              </Section>
            </div>

            <Section
              title="Stripe mapping"
              hint="Optional. Required for paid recurring billing. Copy IDs from Stripe → Product → Pricing."
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input
                  label="Product ID"
                  name="stripe_product_id"
                  value={formData.stripe_product_id}
                  onChange={handleInputChange}
                  error={errors.stripe_product_id}
                  placeholder="prod_…"
                />
                <Input
                  label="Price ID — monthly"
                  name="stripe_price_id_monthly"
                  value={formData.stripe_price_id_monthly}
                  onChange={handleInputChange}
                  error={errors.stripe_price_id_monthly}
                  placeholder="price_…"
                />
                <Input
                  label="Price ID — yearly"
                  name="stripe_price_id_yearly"
                  value={formData.stripe_price_id_yearly}
                  onChange={handleInputChange}
                  error={errors.stripe_price_id_yearly}
                  placeholder="price_…"
                />
              </div>
            </Section>

            <Section title="Marketing features" hint="Bullet points shown on the pricing page.">
              <div className="space-y-2">
                {formData.features_list.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      placeholder="e.g. Priority support"
                      className="flex-1"
                    />
                    {formData.features_list.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="p-2 text-red-500 hover:text-red-700 dark:hover:text-red-400 !rounded-xl hover:bg-red-50 dark:hover:bg-red-950/30"
                        aria-label="Remove feature"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
              {errors.features_list ? (
                <p className="text-sm text-red-600 dark:text-red-400">{errors.features_list}</p>
              ) : null}
              <Button type="button" onClick={addFeature} variant="outline" className="!rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Add feature
              </Button>
            </Section>

            <Section title="Capability flags">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { key: 'feature_analytics', label: 'Analytics' },
                  { key: 'feature_custom_branding', label: 'Custom branding' },
                  { key: 'feature_priority_support', label: 'Priority support' },
                  { key: 'feature_advanced_booking', label: 'Advanced booking' },
                  { key: 'feature_multi_location', label: 'Multi location' },
                  { key: 'feature_ai_generator', label: 'AI service writer' },
                ].map((feature) => (
                  <label
                    key={feature.key}
                    className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2.5 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      name={feature.key}
                      checked={formData[feature.key]}
                      onChange={handleInputChange}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{feature.label}</span>
                  </label>
                ))}
              </div>
            </Section>

            <Section
              title="Vendor page access"
              hint="Pages this plan can open in the vendor dashboard."
            >
              <div className="space-y-3">
                {VENDOR_PAGE_ACCESS_GROUPS.map((group) => (
                  <div
                    key={group.key}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3"
                  >
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                      {group.label}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {group.items.map((item) => (
                        <label key={item.key} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.vendor_page_access.includes(item.key)}
                            onChange={() => toggleAccess(item.key)}
                            className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          <div className="shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={busy}
              className="!rounded-xl"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy} loading={busy} className="!rounded-xl">
              {isEdit ? 'Save changes' : 'Create plan'}
            </Button>
          </div>
        </form>
      </div>
    </ModalShell>
  );
};

export default CreateSubscriptionModal;
